import type {
  MerchantProduct,
  MerchantProductFilters,
  ProductMerchantApiResponse,
  MerchantProductResponse,
  MerchantProductUpdateResponse
} from "../types"
import { transformApiItem } from "./merchantProductTransformers"
import { buildQueryParams, createDefaultFilters, validateProductUpdateData, parseProductMutationError } from "./merchantProductHelpers"
import MerchantProductApiClient from "./merchantProductApiClient"

class MerchantProductService {
  private static instance: MerchantProductService
  private apiClient: MerchantProductApiClient

  private constructor() {
    this.apiClient = new MerchantProductApiClient()
  }

  static getInstance(): MerchantProductService {
    if (!this.instance) {
      this.instance = new MerchantProductService()
    }
    return this.instance
  }


  /**
   * Fetch merchant products from API
   */
  async getMerchantProducts(
    filters: MerchantProductFilters,
    page: number = 1,
    pageSize: number = 20
  ): Promise<MerchantProductResponse> {
    const queryParams = buildQueryParams(filters, page, pageSize)
    
    try {
      const response = await this.apiClient.get("/api/v2/product_merchants", queryParams)

      if (!response.ok) {
        await this.apiClient.parseErrorResponse(response, "Failed to fetch merchant products")
      }

      const data: ProductMerchantApiResponse = await response.json()

      // Transform the response to use camelCase
      return {
        success: data.success,
        data: {
          product_merchants: data.data.product_merchants.map(item =>
            transformApiItem(item)
          ),
          pagination: data.data.pagination
        },
        _meta: data._meta
      }
    } catch (error) {
      console.error("Failed to fetch merchant products:", error)
      throw error
    }
  }

  /**
   * Update product stock
   */
  async updateProductStock(
    productId: string,
    quantity: number
  ): Promise<MerchantProductUpdateResponse> {
    try {
      const response = await this.apiClient.put(
        `/api/v2/product_merchants/${productId}/stock`,
        { quantity }
      )

      if (!response.ok) {
        await this.apiClient.parseErrorResponse(response, "Failed to update stock")
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error("Failed to update product stock:", error)
      throw error
    }
  }

  /**
   * Update product price
   */
  async updateProductPrice(
    productId: string,
    price: number
  ): Promise<MerchantProductUpdateResponse> {
    try {
      const response = await this.apiClient.put(
        `/api/v2/product_merchants/${productId}/price`,
        { price }
      )

      if (!response.ok) {
        await this.apiClient.parseErrorResponse(response, "Failed to update price")
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error("Failed to update product price:", error)
      throw error
    }
  }

  /**
   * Search products by text query
   */
  async searchProducts(
    searchTerm: string,
    page: number = 1,
    pageSize: number = 20
  ): Promise<MerchantProductResponse> {
    const filters = createDefaultFilters({ search: searchTerm })
    return this.getMerchantProducts(filters, page, pageSize)
  }

  /**
   * Get top performing products by analytics metric
   */
  async getTopPerformers(
    metric: "sales_qty_30d" | "sales_revenue_30d" = "sales_revenue_30d",
    limit: number = 20
  ): Promise<MerchantProductResponse> {
    const filters = createDefaultFilters({ sortBy: metric, sortOrder: "desc" })
    return this.getMerchantProducts(filters, 1, limit)
  }

  /**
   * Get products by brand
   */
  async getProductsByBrand(
    brandName: string,
    page: number = 1,
    pageSize: number = 20
  ): Promise<MerchantProductResponse> {
    const filters = createDefaultFilters({ brandName })
    return this.getMerchantProducts(filters, page, pageSize)
  }

  /**
   * Get products by category
   */
  async getProductsByCategory(
    category: string,
    page: number = 1,
    pageSize: number = 20
  ): Promise<MerchantProductResponse> {
    const filters = createDefaultFilters({ category })
    return this.getMerchantProducts(filters, page, pageSize)
  }

  /**
   * Get products by type (single, variant, bundle)
   */
  async getProductsByType(
    productType: 'single' | 'variant' | 'bundle',
    page: number = 1,
    pageSize: number = 20
  ): Promise<MerchantProductResponse> {
    const filters = createDefaultFilters({ productType })
    return this.getMerchantProducts(filters, page, pageSize)
  }

  /**
   * Get products in price range
   */
  async getProductsInPriceRange(
    minPrice: number | null,
    maxPrice: number | null,
    page: number = 1,
    pageSize: number = 20
  ): Promise<MerchantProductResponse> {
    const filters = createDefaultFilters({ 
      priceRange: { min: minPrice, max: maxPrice },
      sortBy: "price"
    })
    return this.getMerchantProducts(filters, page, pageSize)
  }

  /**
   * Create a new product merchant
   */
  async createMerchantProduct(
    productData: any // TODO: Define proper interface for wizard data
  ): Promise<MerchantProductUpdateResponse> {
    try {
      const response = await this.apiClient.post(
        "/api/v2/product_merchants",
        productData
      )

      if (!response.ok) {
        await parseProductMutationError(response, 'create')
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error("Failed to create merchant product:", error)
      throw error
    }
  }

  /**
   * Update an existing product merchant
   */
  async updateMerchantProduct(
    productId: string,
    productData: any // TODO: Define proper interface for wizard data
  ): Promise<MerchantProductUpdateResponse> {
    // Validate product data
    validateProductUpdateData(productId, productData)

    try {
      console.log(`🔄 Updating product ${productId} with data:`, productData)
      
      const response = await this.apiClient.put(
        `/api/v2/product_merchants/${productId}`,
        productData
      )

      if (!response.ok) {
        await parseProductMutationError(response, 'update')
      }

      const data = await response.json()
      console.log(`✅ Product ${productId} updated successfully:`, data)
      return data
    } catch (error) {
      console.error("Failed to update merchant product:", error)
      throw error
    }
  }

  /**
   * Get a single product merchant by ID for editing
   */
  async getProductById(productId: string): Promise<MerchantProduct | null> {
    try {
      // Use search with SKU to find specific product
      const response = await this.apiClient.get(
        "/api/v2/product_merchants",
        `search=${encodeURIComponent(productId)}&page_size=100`
      )

      if (!response.ok) {
        if (response.status === 404) {
          return null
        }
        await this.apiClient.parseErrorResponse(response, "Failed to fetch product")
      }

      const data: ProductMerchantApiResponse = await response.json()

      if (!data.success || !data.data?.product_merchants) {
        return null
      }

      // Find exact match by ID
      const exactMatch = data.data.product_merchants.find(
        product => product.id === productId
      )

      if (!exactMatch) {
        return null
      }

      // Transform the single product to camelCase format
      return transformApiItem(exactMatch)
    } catch (error) {
      console.error(`Failed to fetch product ${productId}:`, error)
      throw error
    }
  }

  /**
   * Get product type counts (for tab display)
   * Makes lightweight API calls to get count for each type
   */
  async getProductTypeCounts(
    baseFilters: Partial<MerchantProductFilters> = {}
  ): Promise<{
    all: number
    variant: number
    single: number  
    bundle: number
  }> {
    const commonFilters = createDefaultFilters({
      search: baseFilters.search,
      brandName: baseFilters.brandName,
      category: baseFilters.category,
      isActive: baseFilters.isActive,
      priceRange: baseFilters.priceRange,
      salesRange: baseFilters.salesRange
    })

    try {
      // Make parallel API calls with minimal page_size to get counts efficiently
      const [allResponse, variantResponse, singleResponse, bundleResponse] = await Promise.all([
        this.getMerchantProducts({ ...commonFilters, productType: undefined }, 1, 1),
        this.getMerchantProducts({ ...commonFilters, productType: 'variant' }, 1, 1),
        this.getMerchantProducts({ ...commonFilters, productType: 'single' }, 1, 1),
        this.getMerchantProducts({ ...commonFilters, productType: 'bundle' }, 1, 1)
      ])

      return {
        all: allResponse.data.pagination.total_count,
        variant: variantResponse.data.pagination.total_count,
        single: singleResponse.data.pagination.total_count,
        bundle: bundleResponse.data.pagination.total_count
      }
    } catch (error) {
      console.error('Failed to fetch product type counts:', error)
      // Return zero counts on error
      return { all: 0, variant: 0, single: 0, bundle: 0 }
    }
  }
}

export default MerchantProductService